/**
 * ORBIT · Oodel Research Builder & Intelligence Tool
 * ---------------------------------------------------
 * Right-rail co-pilot inside the Wave Builder. Light-theme card, matches
 * the newspaper aesthetic of the rest of admin.
 *
 * KEY BEHAVIOURS
 * • State is LIFTED to a parent-provided store (see `useOrbitStore`) so it
 *   survives tab switches WITHOUT re-hitting the API. Radar + Lint auto-
 *   refresh only when the fingerprint of the selected questions changes.
 * • Brief cache lives per-wave (waveKey) in localStorage — click Brief tab
 *   after coming back → same 6 questions, no new Claude call.
 * • Every score has a tooltip explaining what it means (Radar %, Lint 0-100).
 * • TOPIC vs DISCOVERY modes have hover tooltips.
 * • Branding: ORBIT logo + "Powered by oodel·ˣ" (with oodel·ˣ mini-logo).
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Radar, ListChecks, ArrowDownUp, Sparkles,
  Loader2, ChevronDown, ChevronRight, AlertTriangle, CheckCircle2, Info,
  X, Wand2, Plus, Check, RotateCcw,
} from "lucide-react";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../ui/tooltip";

const ORBIT_LOGO = "/brand/orbit.png";
const OODELX_LOGO = "/brand/oodelx.png";

// ── helpers ──────────────────────────────────────────────────────────────
const sevColor = (sev) => sev === "error" ? "text-rose-600"
                       : sev === "warn"  ? "text-amber-600"
                       : "text-sky-600";
const scoreColor = (s) => s >= 90 ? "text-emerald-600"
                       : s >= 75 ? "text-amber-600"
                       : "text-rose-600";
const matchColor = (s) => s >= 0.7 ? "text-rose-600 bg-rose-50 border-rose-200"
                       : s >= 0.5 ? "text-amber-700 bg-amber-50 border-amber-200"
                       : "text-sky-700 bg-sky-50 border-sky-200";

// Fingerprint the current selected questions so Radar/Lint know when to
// refresh. Same selection = same fingerprint = no re-call.
const fingerprint = (qs) =>
  (qs || []).map((q) => `${q.id || ""}::${(q.text || "").length}`).join("|");

// ── HOOK: orbit store ────────────────────────────────────────────────────
// Persistent store for the whole rail — brief, radar, lint, order, tab.
// Key by wave_id so different waves get their own cache.
export function useOrbitStore(waveKey = "new") {
  const storageKey = `orbit_cache_v1::${waveKey}`;
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) return JSON.parse(raw);
    } catch { /* fall through */ }
    return {
      tab: "brief",
      brief: null,           // Full brief object OR null
      briefForm: { topic: "", audience: "", count: 8, mode: "topic" },
      openIdx: 0,            // Which brief question is expanded
      imported: {},          // { qIdx: questionDoc } — per brief-question, what was imported
      radar: { fp: "", results: [], ranOnce: false },
      lint:  { fp: "", items: [] },
      order: { fp: "", suggested: null },
    };
  });

  // Persist on every change (small blob, cheap).
  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(state)); } catch { /* quota exceeded — safe to ignore */ }
  }, [state, storageKey]);

  const patch = useCallback((delta) => {
    setState((s) => ({ ...s, ...(typeof delta === "function" ? delta(s) : delta) }));
  }, []);

  return [state, patch];
}

// ── brand marks ──────────────────────────────────────────────────────────
function OrbitMark({ size = 32 }) {
  return (
    <img
      src={ORBIT_LOGO}
      alt="ORBIT"
      width={size}
      height={size}
      className="orbit-mark-spin block"
      style={{ display: "block" }}
    />
  );
}

function OodelxTag() {
  return (
    <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.14em] text-neutral-500 font-mono">
      <span>Powered by</span>
      <img src={OODELX_LOGO} alt="oodel·ˣ" height={11} className="opacity-80" style={{ height: 11 }} />
    </div>
  );
}

// Reusable tooltip icon
function InfoTip({ children, side = "left" }) {
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" className="text-neutral-400 hover:text-black -my-1" tabIndex={-1}>
            <Info size={11} />
          </button>
        </TooltipTrigger>
        <TooltipContent side={side} className="max-w-xs text-[11px] leading-relaxed bg-black text-white border-2 border-black rounded-none">
          {children}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ────────────────────────────────────────────────────────────────────────
// 1. Radar
// ────────────────────────────────────────────────────────────────────────
function RadarPanel({ selectedQuestions, store, patch }) {
  const fp = useMemo(() => fingerprint(selectedQuestions), [selectedQuestions]);
  const stale = store.radar.fp !== fp;
  const [loading, setLoading] = useState(false);

  const texts = useMemo(
    () => (selectedQuestions || []).map((q) => q.text).filter(Boolean),
    [selectedQuestions]
  );
  const ids = useMemo(
    () => (selectedQuestions || []).map((q) => q.id).filter(Boolean),
    [selectedQuestions]
  );

  const scan = useCallback(async () => {
    if (!texts.length) {
      patch({ radar: { fp, results: [], ranOnce: true } });
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/admin/orbit/duplicates", {
        texts, exclude_question_ids: ids, top_k: 3, min_score: 0.35,
      });
      patch({ radar: { fp, results: data.results || [], ranOnce: true } });
    } catch { toast.error("ORBIT radar failed"); }
    finally { setLoading(false); }
  }, [texts, ids, fp, patch]);

  // Only auto-scan when the fingerprint changes.
  useEffect(() => {
    if (!stale) return;
    const t = setTimeout(scan, 300);
    return () => clearTimeout(t);
  }, [stale, scan]);

  const results = store.radar.results;
  const totalDupes = results.reduce((n, r) => n + r.matches.filter((m) => m.score >= 0.5).length, 0);

  return (
    <div className="space-y-3" data-testid="orbit-radar">
      <PanelHeader
        title="Duplicate radar"
        loading={loading}
        hint={
          <>
            <div className="font-bold mb-1">What the % means</div>
            We compare each of your selected questions against every question in the bank using character-n-gram similarity.
            <div className="mt-1 space-y-0.5">
              <div><span className="text-rose-300 font-bold">70–100%</span> — near-duplicate. Consider removing.</div>
              <div><span className="text-amber-300 font-bold">50–69%</span> — very similar. Worth reviewing.</div>
              <div><span className="text-sky-300 font-bold">35–49%</span> — related. Usually fine.</div>
            </div>
          </>
        }
        right={!loading && (
          <button type="button" onClick={scan}
            className="text-[10px] uppercase tracking-wider text-neutral-500 hover:text-mango font-mono flex items-center gap-1"
            data-testid="orbit-radar-rescan"><RotateCcw size={10} /> rescan</button>
        )} />

      {!texts.length && <EmptyHint>Add questions to scan for near-duplicates in your bank.</EmptyHint>}

      {store.radar.ranOnce && texts.length > 0 && totalDupes === 0 && (
        <GoodPill>No close matches in your question bank.</GoodPill>
      )}

      <div className="space-y-2 max-h-[44vh] overflow-y-auto pr-1 orbit-scroll">
        {results.map((row, i) => (
          row.matches.length > 0 && (
            <div key={i} className="border border-neutral-200 bg-neutral-50/60 p-2.5" data-testid={`orbit-radar-row-${i}`}>
              <div className="text-[11px] text-neutral-500 mb-1.5 line-clamp-2">
                <span className="font-mono text-neutral-400">Yours:</span> {row.text}
              </div>
              <div className="space-y-1.5">
                {row.matches.map((m, j) => (
                  <div key={j} className="flex items-start justify-between gap-2 text-[11px] border-t border-neutral-200 pt-1.5">
                    <div className="flex-1 text-neutral-800 line-clamp-2">
                      <span className="font-mono text-neutral-400">In bank:</span> {m.text}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className={`border px-1.5 py-0.5 font-mono text-[10px] ${matchColor(m.score)}`}>
                        {Math.round(m.score * 100)}%
                      </span>
                      <span className="text-[9px] uppercase tracking-wider text-neutral-500">{m.verdict?.replace("_", " ")}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        ))}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// 2. Lint
// ────────────────────────────────────────────────────────────────────────
function LintPanel({ selectedQuestions, store, patch }) {
  const fp = useMemo(() => fingerprint(selectedQuestions), [selectedQuestions]);
  const stale = store.lint.fp !== fp;
  const [loading, setLoading] = useState(false);

  const run = useCallback(async () => {
    if (!selectedQuestions?.length) { patch({ lint: { fp, items: [] } }); return; }
    setLoading(true);
    try {
      // NOTE: the bank canonicalises options as `{key, text}`, but the lint
      // API expects `{label}`. Translate both keys so the lint is correct
      // (fixes the false-positive "need at least 2 options" bug).
      const { data } = await api.post("/admin/orbit/lint/batch", {
        items: selectedQuestions.map((q) => ({
          text: q.text || "",
          qtype: q.format || q.qtype,
          options: (q.options || []).map((o) => ({ label: o.label || o.text || "" })),
        })),
      });
      patch({ lint: { fp, items: data.items || [] } });
    } catch { toast.error("ORBIT lint failed"); }
    finally { setLoading(false); }
  }, [selectedQuestions, fp, patch]);

  useEffect(() => {
    if (!stale) return;
    const t = setTimeout(run, 300);
    return () => clearTimeout(t);
  }, [stale, run]);

  const items = store.lint.items;
  const avgScore = items.length
    ? Math.round(items.reduce((s, it) => s + it.score, 0) / items.length)
    : null;
  const totalIssues = items.reduce((s, it) => s + (it.issues?.length || 0), 0);

  return (
    <div className="space-y-3" data-testid="orbit-lint">
      <PanelHeader
        title="Methodology lint"
        loading={loading}
        hint={
          <>
            <div className="font-bold mb-1">What the score means</div>
            0–100 methodological-quality score based on Pew / Gallup / ICC-ESOMAR best practice. Each question starts at 100 and loses points for:
            <div className="mt-1 space-y-0.5">
              <div>• <span className="text-rose-300">–15</span> per <b>error</b> (empty text, duplicate options)</div>
              <div>• <span className="text-amber-300">–8</span> per <b>warn</b> (double-barrel, leading, long stem)</div>
              <div>• <span className="text-sky-300">–3</span> per <b>info</b> (vague qualifier, no &quot;Don&apos;t know&quot;)</div>
            </div>
            <div className="mt-1">
              <span className="text-emerald-300 font-bold">90+</span> excellent · <span className="text-amber-300 font-bold">75–89</span> acceptable · <span className="text-rose-300 font-bold">&lt;75</span> needs fixing
            </div>
          </>
        }
        right={avgScore !== null && (
          <span className={`font-mono text-lg font-black ${scoreColor(avgScore)}`} data-testid="orbit-lint-score">
            {avgScore}
          </span>
        )} />

      {!selectedQuestions?.length && <EmptyHint>Add questions to see methodology scores.</EmptyHint>}

      {items.length > 0 && (
        <>
          <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-mono">
            {totalIssues === 0
              ? "✓ no issues across all questions"
              : `${totalIssues} issue${totalIssues === 1 ? "" : "s"} · ${items.length} question${items.length === 1 ? "" : "s"}`}
          </div>
          <div className="space-y-2 max-h-[44vh] overflow-y-auto pr-1 orbit-scroll">
            {items.map((it, i) => (
              <div key={i} className="border border-neutral-200 p-2.5 bg-white" data-testid={`orbit-lint-q-${i}`}>
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="text-[11px] text-neutral-800 line-clamp-2 flex-1">
                    <span className="text-neutral-400 font-mono mr-1">Q{i + 1}</span>{it.text}
                  </div>
                  <span className={`font-mono text-xs font-bold ${scoreColor(it.score)}`}>{it.score}</span>
                </div>
                {(it.issues || []).length === 0 ? (
                  <div className="flex items-center gap-1.5 text-[10px] text-emerald-600">
                    <CheckCircle2 size={11} /> clean
                  </div>
                ) : (
                  <ul className="space-y-1">
                    {it.issues.map((iss, k) => (
                      <li key={k} className="flex items-start gap-1.5 text-[10.5px] leading-snug">
                        <AlertTriangle size={11} className={`mt-0.5 shrink-0 ${sevColor(iss.severity)}`} />
                        <span className="text-neutral-700">{iss.message}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// 3. Order
// ────────────────────────────────────────────────────────────────────────
function OrderPanel({ selectedQuestions, onApplyOrder, store, patch }) {
  const fp = useMemo(() => fingerprint(selectedQuestions), [selectedQuestions]);
  const stale = store.order.fp !== fp;
  const [loading, setLoading] = useState(false);

  const run = useCallback(async () => {
    if (!selectedQuestions?.length) { patch({ order: { fp, suggested: null } }); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/admin/orbit/suggest-order", {
        items: selectedQuestions.map((q) => ({ id: q.id, text: q.text, qtype: q.format || q.qtype })),
      });
      patch({ order: { fp, suggested: data } });
    } catch { toast.error("ORBIT order failed"); }
    finally { setLoading(false); }
  }, [selectedQuestions, fp, patch]);

  useEffect(() => {
    if (!stale) return;
    const t = setTimeout(run, 300);
    return () => clearTimeout(t);
  }, [stale, run]);

  const suggested = store.order.suggested;
  const moves = suggested?.rationale?.length || 0;

  return (
    <div className="space-y-3" data-testid="orbit-order">
      <PanelHeader
        title="Suggested order"
        loading={loading}
        hint={
          <>
            <div className="font-bold mb-1">How order is chosen</div>
            Following Pew/Gallup convention:
            <div className="mt-1 space-y-0.5">
              <div>1. <b>Warm-up</b> — broad, easy, non-sensitive</div>
              <div>2. <b>Core topic</b> — the main questions</div>
              <div>3. <b>Sensitive</b> — politics, religion, income</div>
              <div>4. <b>Demographics</b> — age, gender, region (LAST)</div>
            </div>
            Click <b>Apply</b> to reorder the wave automatically.
          </>
        }
        right={moves > 0 && (
          <button type="button"
            onClick={() => onApplyOrder?.(suggested.ordered.map((q) => q.id))}
            className="text-[10px] uppercase tracking-wider font-bold text-mango hover:underline"
            data-testid="orbit-order-apply">Apply →</button>
        )} />

      {!selectedQuestions?.length && <EmptyHint>Add questions to see suggested ordering.</EmptyHint>}

      {suggested && (
        <>
          <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-mono">{suggested.flow}</div>
          {moves === 0 ? (
            <GoodPill>Order looks good — no moves suggested.</GoodPill>
          ) : (
            <div className="space-y-1.5 max-h-[44vh] overflow-y-auto pr-1 orbit-scroll">
              {suggested.ordered.map((q, i) => (
                <div key={q.id || i} className="flex items-start gap-2 text-[11px] border border-neutral-200 bg-white p-2">
                  <span className="font-mono text-mango shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-neutral-800 line-clamp-2 flex-1">{q.text}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// 4. Brief — generate + per-question import (cached in store)
// ────────────────────────────────────────────────────────────────────────
function BriefPanel({ onAddOneToWave, defaults, store, patch }) {
  const { briefForm, brief, openIdx, imported } = store;
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState({}); // qIdx: true
  const briefRef = useRef(brief);
  briefRef.current = brief;

  const setForm = (delta) => patch({ briefForm: { ...briefForm, ...delta } });

  const generate = async () => {
    if (!briefForm.topic.trim()) { toast.error("Add a topic first"); return; }
    setLoading(true);
    patch({ brief: null, imported: {}, openIdx: 0 });
    try {
      const { data } = await api.post("/admin/orbit/generate", {
        topic: briefForm.topic.trim(),
        audience: briefForm.audience.trim() || null,
        question_count: Number(briefForm.count) || 8,
        mode: briefForm.mode,
      });
      if (data.error) toast.error(data.error);
      else { patch({ brief: data, imported: {}, openIdx: 0 }); toast.success("ORBIT drafted your survey"); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally { setLoading(false); }
  };

  const addOne = async (idx) => {
    const b = briefRef.current;
    if (!b || imported[idx] || importing[idx]) return;
    const q = b.questions[idx];
    setImporting((s) => ({ ...s, [idx]: true }));
    try {
      const { data } = await api.post("/admin/orbit/import-question", {
        text: q.text,
        qtype: q.qtype || "single",
        options: q.options || null,
        rationale: q.rationale || null,
        pillars: q.pillars || [],
        orbit_brief_id: b.id,
      });
      patch((s) => ({ imported: { ...s.imported, [idx]: data } }));
      onAddOneToWave?.(data);
      toast.success("Added to wave");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not import this question");
    } finally {
      setImporting((s) => ({ ...s, [idx]: false }));
    }
  };

  const clearBrief = () => {
    if (!confirm("Clear this ORBIT brief? You'll need to regenerate to see it again.")) return;
    patch({ brief: null, imported: {}, openIdx: 0 });
  };

  return (
    <div className="space-y-3" data-testid="orbit-brief">
      <PanelHeader
        title="Brief → survey"
        hint="ORBIT drafts a full survey from your topic using Claude Sonnet. Every question declares which oodel·ˣ pillar it powers so the final report has material for SIGNALS, TENSIONS, SEGMENTS and TRAJECTORIES."
        right={brief && (
          <button type="button" onClick={clearBrief}
            className="text-[10px] uppercase tracking-wider text-neutral-500 hover:text-rose-600 font-mono"
            data-testid="orbit-brief-clear">clear</button>
        )}
      />

      <div>
        <label className="text-[10px] uppercase tracking-wider text-neutral-500 font-mono block mb-1">Topic</label>
        <textarea
          value={briefForm.topic} onChange={(e) => setForm({ topic: e.target.value })} rows={2}
          placeholder="e.g. consumer trust in mobile banking"
          className="w-full bg-white border border-neutral-300 text-neutral-900 text-xs px-2 py-1.5 focus:border-mango focus:outline-none placeholder:text-neutral-400"
          data-testid="orbit-brief-topic"
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-neutral-500 font-mono block mb-1">Audience</label>
          <input
            type="text" value={briefForm.audience} onChange={(e) => setForm({ audience: e.target.value })}
            placeholder={defaults?.audience || "optional"}
            className="w-full bg-white border border-neutral-300 text-neutral-900 text-xs px-2 py-1.5 focus:border-mango focus:outline-none placeholder:text-neutral-400"
            data-testid="orbit-brief-audience"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-neutral-500 font-mono block mb-1"># Qs</label>
          <input
            type="number" min={3} max={15} value={briefForm.count}
            onChange={(e) => setForm({ count: e.target.value })}
            className="w-full bg-white border border-neutral-300 text-neutral-900 text-xs px-2 py-1.5 focus:border-mango focus:outline-none font-mono"
            data-testid="orbit-brief-count"
          />
        </div>
      </div>

      {/* Mode toggle with clear labels + tooltips */}
      <div>
        <label className="text-[10px] uppercase tracking-wider text-neutral-500 font-mono block mb-1">Mode</label>
        <div className="grid grid-cols-2 gap-1.5">
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" onClick={() => setForm({ mode: "topic" })}
                  className={`text-[10px] uppercase tracking-wider px-2 py-1.5 border-2 font-bold transition-colors text-left ${
                    briefForm.mode === "topic" ? "bg-mango text-white border-mango" : "border-neutral-300 text-neutral-600 hover:border-black"
                  }`}
                  data-testid="orbit-brief-mode-topic">
                  Topic
                  <div className="text-[9px] font-normal opacity-80 normal-case tracking-normal mt-0.5">direct · focused</div>
                </button>
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-xs text-[11px] leading-relaxed bg-black text-white border-2 border-black rounded-none">
                <div className="font-bold mb-1">Topic mode</div>
                Focused, direct research on the exact topic you specify. ORBIT sticks closely to the subject — best when you already know what you want to measure.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" onClick={() => setForm({ mode: "discovery" })}
                  className={`text-[10px] uppercase tracking-wider px-2 py-1.5 border-2 font-bold transition-colors text-left ${
                    briefForm.mode === "discovery" ? "bg-mango text-white border-mango" : "border-neutral-300 text-neutral-600 hover:border-black"
                  }`}
                  data-testid="orbit-brief-mode-discovery">
                  Discovery
                  <div className="text-[9px] font-normal opacity-80 normal-case tracking-normal mt-0.5">exploratory · wide net</div>
                </button>
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-xs text-[11px] leading-relaxed bg-black text-white border-2 border-black rounded-none">
                <div className="font-bold mb-1">Discovery mode</div>
                Exploratory net-casting around the topic. ORBIT probes adjacent areas (attitudes, behaviours, motivations, blockers) to surface unknown-unknowns — best when you&apos;re still shaping the research question.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <button type="button" onClick={generate}
        disabled={loading || !briefForm.topic.trim()}
        className="w-full bg-black hover:bg-mango disabled:opacity-40 disabled:hover:bg-black text-white font-bold text-xs uppercase tracking-wider py-2 flex items-center justify-center gap-2 border-2 border-black transition-colors"
        data-testid="orbit-brief-generate">
        {loading ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
        {loading ? "ORBIT drafting…" : brief ? "Regenerate" : "Generate survey"}
      </button>

      {brief && (
        <div className="mt-2 border-2 border-black bg-white">
          <div className="px-3 py-2 border-b-2 border-black bg-mango/5">
            <div className="text-[10px] uppercase tracking-wider font-mono text-mango font-bold">{brief.title}</div>
            <div className="text-[11px] text-neutral-700 mt-0.5 line-clamp-2">{brief.objective}</div>
          </div>

          {brief.tension_pairs?.length > 0 && (
            <div className="px-3 py-1.5 bg-neutral-50 border-b border-neutral-200 text-[10px] text-neutral-600 italic">
              <strong>{brief.tension_pairs.length}</strong> attitude↔behaviour tension{brief.tension_pairs.length === 1 ? "" : "s"} built in — perfect for the report&apos;s TENSIONS section.
            </div>
          )}

          <div className="max-h-[42vh] overflow-y-auto orbit-scroll">
            {(brief.questions || []).map((q, i) => {
              const lint = brief.lint?.[i];
              const isOpen = openIdx === i;
              const isAdded = !!imported[i];
              const isLoading = !!importing[i];
              const isText = (q.qtype || "").toLowerCase() === "text";
              return (
                <div key={i} className="border-b border-neutral-200 last:border-b-0" data-testid={`orbit-brief-q-${i}`}>
                  <button type="button"
                    onClick={() => patch({ openIdx: isOpen ? null : i })}
                    className="w-full px-3 py-2 text-left flex items-start gap-2 hover:bg-neutral-50 transition-colors">
                    {isOpen ? <ChevronDown size={13} className="mt-0.5 shrink-0 text-neutral-400" />
                            : <ChevronRight size={13} className="mt-0.5 shrink-0 text-neutral-400" />}
                    <span className="font-mono text-[10px] text-neutral-400 mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-[11px] text-neutral-800 flex-1 line-clamp-2">{q.text}</span>
                    {isAdded && <Check size={11} className="mt-0.5 text-emerald-600 shrink-0" />}
                    {lint && !isAdded && (
                      <span className={`font-mono text-[10px] shrink-0 ${scoreColor(lint.score)}`}>{lint.score}</span>
                    )}
                  </button>

                  {isOpen && (
                    <div className="px-3 pb-3 pt-1 space-y-2 bg-neutral-50/60">
                      <div className="flex flex-wrap gap-1 text-[9px] uppercase tracking-wider font-mono">
                        <span className="border border-neutral-300 bg-white px-1.5 py-0.5">{q.qtype}</span>
                        {(q.pillars || []).map((p) => (
                          <span key={p} className="border border-mango/40 bg-mango/10 text-mango px-1.5 py-0.5">
                            oodel·ˣ · {p}
                          </span>
                        ))}
                      </div>

                      {q.rationale && (
                        <div className="text-[10.5px] text-neutral-600 italic">{q.rationale}</div>
                      )}

                      {/* Options preview */}
                      {(q.options?.length > 0 || q.qtype === "yesno" || q.qtype === "rating") && (
                        <div className="space-y-1">
                          {(q.options && q.options.length > 0
                            ? q.options
                            : q.qtype === "yesno"
                              ? [{ label: "Yes" }, { label: "No" }, { label: "Not sure" }]
                              : q.qtype === "rating"
                                ? [{ label: "1 — Very poor" }, { label: "2" }, { label: "3" }, { label: "4" }, { label: "5 — Excellent" }]
                                : []
                          ).map((o, k) => (
                            <div key={k} className="flex items-center gap-2 text-[11px] text-neutral-700 bg-white border border-neutral-200 px-2 py-1">
                              <span className="font-mono text-[9px] text-neutral-400 w-4">{String.fromCharCode(65 + k)}</span>
                              {o.label || o.text}
                            </div>
                          ))}
                        </div>
                      )}

                      {(lint?.issues || []).length > 0 && (
                        <ul className="space-y-1">
                          {lint.issues.map((iss, k) => (
                            <li key={k} className="flex items-start gap-1.5 text-[10px]">
                              <AlertTriangle size={10} className={`mt-0.5 shrink-0 ${sevColor(iss.severity)}`} />
                              <span className="text-neutral-700">{iss.message}</span>
                            </li>
                          ))}
                        </ul>
                      )}

                      <button type="button" onClick={() => addOne(i)}
                        disabled={isAdded || isLoading || isText}
                        title={isText ? "Open-ended questions can't be auto-imported. Add manually from Question Bank." : ""}
                        className={`w-full mt-1 font-bold text-[11px] uppercase tracking-wider py-1.5 flex items-center justify-center gap-1.5 border-2 transition-colors ${
                          isAdded
                            ? "bg-emerald-50 border-emerald-500 text-emerald-700 cursor-default"
                            : isText
                              ? "bg-neutral-100 border-neutral-300 text-neutral-400 cursor-not-allowed"
                              : "bg-white border-black text-black hover:bg-mango hover:text-white hover:border-mango"
                        }`}
                        data-testid={`orbit-brief-add-${i}`}>
                        {isLoading
                          ? <><Loader2 size={11} className="animate-spin" /> adding…</>
                          : isAdded
                            ? <><Check size={11} /> added to wave</>
                            : isText
                              ? <>open-ended · add manually</>
                              : <><Plus size={11} /> add to wave</>}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Shared subviews
// ────────────────────────────────────────────────────────────────────────
function PanelHeader({ title, right, loading, hint }) {
  return (
    <div className="flex items-center justify-between">
      <div className="text-[10px] uppercase tracking-[0.15em] font-mono text-neutral-500 flex items-center gap-1.5">
        {title}
        {loading && <Loader2 size={10} className="animate-spin" />}
        {hint && <InfoTip>{hint}</InfoTip>}
      </div>
      {right}
    </div>
  );
}
function EmptyHint({ children }) {
  return <div className="text-xs text-neutral-500 italic py-6 text-center border border-dashed border-neutral-300 bg-neutral-50">{children}</div>;
}
function GoodPill({ children }) {
  return (
    <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2">
      <CheckCircle2 size={14} /> {children}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// The Rail
// ────────────────────────────────────────────────────────────────────────
const TABS = [
  { key: "brief", label: "Brief", Icon: Sparkles },
  { key: "radar", label: "Radar", Icon: Radar },
  { key: "lint",  label: "Lint",  Icon: ListChecks },
  { key: "order", label: "Order", Icon: ArrowDownUp },
];

export function OrbitRail({
  selectedQuestions = [],
  onApplyOrder,
  onAddOneToWave,
  defaults,
  enabled = true,
  onClose,
  store,
  patch,
}) {
  if (!enabled) return null;
  const tab = store?.tab || "brief";
  const setTab = (v) => patch({ tab: v });

  return (
    <aside className="orbit-rail w-full text-neutral-900" data-testid="orbit-rail">
      <div className="bg-white border-2 border-black hard-shadow">
        {/* Header — full-bleed black band with ORBIT wordmark left,
            oodel·ˣ FRAMEWORK lockup right (per user's mock). */}
        <div className="bg-black px-3 py-2.5 border-b-2 border-black flex items-center justify-between gap-2">
          <img
            src="/brand/orbit-wordmark.png"
            alt="ORBIT"
            style={{ height: 22, width: "auto", filter: "invert(1) brightness(2)" }}
          />
          <div className="flex items-center gap-2 min-w-0">
            <img
              src="/brand/oodelx-framework.png"
              alt="oodel·ˣ framework"
              style={{ height: 22, width: "auto", filter: "invert(1) brightness(2)" }}
            />
            {onClose && (
              <button type="button" onClick={onClose}
                className="text-white/60 hover:text-white p-1 -mr-1"
                data-testid="orbit-close" aria-label="Close ORBIT">
                <X size={13} />
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-4 border-b-2 border-black bg-neutral-50">
          {TABS.map((t) => {
            const active = tab === t.key;
            const Icon = t.Icon;
            return (
              <button key={t.key} type="button" onClick={() => setTab(t.key)}
                className={`relative flex flex-col items-center gap-0.5 py-2 text-[9px] uppercase tracking-[0.12em] font-bold transition-colors ${
                  active ? "text-mango bg-white" : "text-neutral-500 hover:text-black hover:bg-white"
                }`}
                data-testid={`orbit-tab-${t.key}`}>
                <Icon size={13} strokeWidth={1.8} />
                {t.label}
                {active && <span className="absolute -bottom-px left-2 right-2 h-0.5 bg-mango" />}
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div className="p-3 bg-white">
          {tab === "brief" && <BriefPanel onAddOneToWave={onAddOneToWave} defaults={defaults} store={store} patch={patch} />}
          {tab === "radar" && <RadarPanel selectedQuestions={selectedQuestions} store={store} patch={patch} />}
          {tab === "lint"  && <LintPanel  selectedQuestions={selectedQuestions} store={store} patch={patch} />}
          {tab === "order" && <OrderPanel selectedQuestions={selectedQuestions} onApplyOrder={onApplyOrder} store={store} patch={patch} />}
        </div>

        <div className="px-3 py-1.5 border-t border-neutral-200 flex items-center justify-between text-[9px] uppercase tracking-[0.18em] text-neutral-400 font-mono">
          <span>v1.2</span>
          <span>ORBIT · oodel·ˣ</span>
        </div>
      </div>
    </aside>
  );
}

export default OrbitRail;
